import React from "react";
import { PrincipalFormData, PrincipalFormErrors } from "../types";

interface ContactInformationFormProps {
  formData: PrincipalFormData;
  formErrors: PrincipalFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

const ContactInformationForm: React.FC<ContactInformationFormProps> = ({
  formData,
  formErrors,
  onChange,
}) => {
  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
      <h2 className="text-lg font-semibold mb-4">
        Contact Information
      </h2>
      <div className="space-y-6">
        {/* Contact Email and Phone Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="contactInformation.email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <input
              id="contactInformation.email"
              type="email"
              name="contactInformation.email"
              value={formData.contactInformation.email}
              onChange={onChange}
              placeholder="Enter Contact Email"
              className={`block w-full px-3 py-2 border ${formErrors.contactInformation.email ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.contactInformation.email && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.contactInformation.email}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="contactInformation.phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              id="contactInformation.phone"
              type="text"
              name="contactInformation.phone"
              value={formData.contactInformation.phone}
              onChange={onChange}
              placeholder="Enter Contact Phone"
              className={`block w-full px-3 py-2 border ${formErrors.contactInformation.phone ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.contactInformation.phone && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.contactInformation.phone}
              </div>
            )}
          </div>
        </div>

        {/* Contact Person */}
        <div>
          <label htmlFor="contactInformation.contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
            Contact Person
          </label>
          <input
            id="contactInformation.contactPerson"
            type="text"
            name="contactInformation.contactPerson"
            value={formData.contactInformation.contactPerson}
            onChange={onChange}
            placeholder="Enter Contact Person"
            className={`block w-full px-3 py-2 border ${formErrors.contactInformation.contactPerson ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
          />
          {formErrors.contactInformation.contactPerson && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.contactInformation.contactPerson}
            </div>
          )}
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Address</h3>

          {/* Street */}
          <div>
            <label htmlFor="contactInformation.address.street" className="block text-sm font-medium text-gray-700 mb-2">
              Street
            </label>
            <input
              id="contactInformation.address.street"
              type="text"
              name="contactInformation.address.street"
              value={formData.contactInformation.address.street}
              onChange={onChange}
              placeholder="Enter Street"
              className={`block w-full px-3 py-2 border ${formErrors.contactInformation.address.street ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.contactInformation.address.street && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.contactInformation.address.street}
              </div>
            )}
          </div>

          {/* City, State, Country Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="contactInformation.address.city" className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                id="contactInformation.address.city"
                type="text"
                name="contactInformation.address.city"
                value={formData.contactInformation.address.city}
                onChange={onChange}
                placeholder="Enter City"
                className={`block w-full px-3 py-2 border ${formErrors.contactInformation.address.city ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
              />
              {formErrors.contactInformation.address.city && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.contactInformation.address.city}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="contactInformation.address.state" className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <input
                id="contactInformation.address.state"
                type="text"
                name="contactInformation.address.state"
                value={formData.contactInformation.address.state}
                onChange={onChange}
                placeholder="Enter State"
                className={`block w-full px-3 py-2 border ${formErrors.contactInformation.address.state ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
              />
              {formErrors.contactInformation.address.state && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.contactInformation.address.state}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="contactInformation.address.country" className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <input
                id="contactInformation.address.country"
                type="text"
                name="contactInformation.address.country"
                value={formData.contactInformation.address.country}
                onChange={onChange}
                placeholder="Enter Country"
                className={`block w-full px-3 py-2 border ${formErrors.contactInformation.address.country ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
              />
              {formErrors.contactInformation.address.country && (
                <div className="text-red-500 text-sm mt-1">
                  {formErrors.contactInformation.address.country}
                </div>
              )}
            </div>
          </div>

          {/* Zip Code */}
          <div>
            <label htmlFor="contactInformation.address.zipCode" className="block text-sm font-medium text-gray-700 mb-2">
              Zip Code
            </label>
            <input
              id="contactInformation.address.zipCode"
              type="text"
              name="contactInformation.address.zipCode"
              value={formData.contactInformation.address.zipCode}
              onChange={onChange}
              placeholder="Enter Zip Code"
              className={`block w-full px-3 py-2 border ${formErrors.contactInformation.address.zipCode ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.contactInformation.address.zipCode && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.contactInformation.address.zipCode}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactInformationForm;

